import { supabase } from '@/integrations/supabase/client';

const SESSION_KEY = 'analytics-session-id';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const GEO_CACHE_KEY = 'analytics-geo-data';
const GEO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface GeoData {
  country: string | null;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  cachedAt: number;
}

const detectPlatform = (): string => {
  const ua = navigator.userAgent;
  
  if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
};

const detectDeviceType = (): string => {
  const ua = navigator.userAgent;
  
  // Check for tablets first (before mobile, since some tablets have 'Mobile' in UA)
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return 'Tablet';
  // Check for mobile devices
  if (/Mobile|iPhone|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'Mobile';
  // Default to desktop
  return 'Desktop';
};

interface StoredSession {
  id: string;
  lastActivity: number;
}

const getStoredSession = (): StoredSession | null => {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

const storeSession = (session: StoredSession): void => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

const getCachedGeoData = (): GeoData | null => {
  try {
    const stored = localStorage.getItem(GEO_CACHE_KEY);
    if (stored) {
      const data: GeoData = JSON.parse(stored);
      if (Date.now() - data.cachedAt < GEO_CACHE_DURATION) {
        return data;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
};

const cacheGeoData = (data: GeoData): void => {
  localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(data));
};

const fetchGeoData = async (): Promise<GeoData> => {
  // Check cache first
  const cached = getCachedGeoData();
  if (cached) {
    return cached;
  }

  // Return empty data helper function
  const getEmptyData = (): GeoData => ({
    country: null,
    countryCode: null,
    region: null,
    city: null,
    cachedAt: Date.now(),
  });

  try {
    // Use ipapi.co which provides free HTTPS access (1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    
    if (!response.ok) {
      // Silently fail - geo data is optional for analytics
      return getEmptyData();
    }

    const data = await response.json();
    
    // Check for rate limit or error response
    if (data.error || data.reason) {
      return getEmptyData();
    }
    
    const geoData: GeoData = {
      country: data.country_name || null,
      countryCode: data.country_code || null,
      region: data.region || null,
      city: data.city || null,
      cachedAt: Date.now(),
    };
    cacheGeoData(geoData);
    return geoData;
  } catch {
    // Silently fail - geo data is optional for analytics
    // Don't log error to avoid console pollution
    return getEmptyData();
  }
};

export const getOrCreateSession = async (): Promise<string | null> => {
  try {
    const now = Date.now();
    const storedSession = getStoredSession();
    
    // Check if we have a valid session that hasn't timed out
    if (storedSession && (now - storedSession.lastActivity) < SESSION_TIMEOUT) {
      // Update last activity
      storeSession({ ...storedSession, lastActivity: now });
      
      // Update session in database via security-definer RPC
      await supabase.rpc('touch_user_session', { _session_token: storedSession.id });
      
      return storedSession.id;
    }
    
    // Create new session
    const { data: { user } } = await supabase.auth.getUser();
    
    const sessionToken = crypto.randomUUID();
    
    const { error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: user?.id || null,
        session_token: sessionToken,
        user_agent: navigator.userAgent,
        platform: detectPlatform(),
      });
    
    if (error) {
      console.error('Error creating session:', error);
      return null;
    }
    
    storeSession({ id: sessionToken, lastActivity: now });
    return sessionToken;
  } catch (error) {
    console.error('Error managing session:', error);
    return null;
  }
};

export const trackPageView = async (path: string): Promise<boolean> => {
  try {
    const [sessionId, geoData] = await Promise.all([
      getOrCreateSession(),
      fetchGeoData(),
    ]);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('page_views')
      .insert({
        user_id: user?.id || null,
        path,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        session_id: sessionId,
        country: geoData.country,
        country_code: geoData.countryCode,
        region: geoData.region,
        city: geoData.city,
        device_type: detectDeviceType(),
      });
    
    if (error) {
      console.error('Error tracking page view:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error tracking page view:', error);
    return false;
  }
};
